import { alertController, modalController, toastController } from '@ionic/core';
import { Component, Element, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import {
  Constants,
  DateUtils,
  FeedItemSubType,
  FeedItemType,
  FileDoc,
  FileType,
  PhotoAnalysis,
  PhotoDoc,
  PersonDoc,
  ProjectDoc,
  RoleUtils,
  SubmittalDoc,
  SubmittalFeedItemDoc,
  SubmittalRevision,
  TaskDoc,
  TaskRelationship,
  WorkflowState,
  WorkflowStateComplete,
} from 'common';
import { ChangeEvent, ConcourseDb, DbListener } from 'src/global/ConcourseDb';
import { ScheduleData } from '../../components/concourse-schedule/model/ScheduleData';
import { SubmittalService } from 'src/submittals/services/SubmittalService';
import { DateSelector } from 'src/global/ui/date-selector';
import { User } from '../../global/models';
import { FileService } from '../../global/service/FileService';
import { ConcourseMessageModal } from '../../components/common/concourse-message-modal';
import { DocumentViewerModal } from '../../components/concourse-documents/document-viewer-modal';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';
import { TimelineUtils, WorkflowUtils, SharedUtils, peopleState, FeedService, ScheduleService, PeopleService } from '../../global';
import { ProjectService } from '../../projects/services/ProjectService';
import { Calendar } from '../../components/concourse-schedule/model/Calendar';
import { SubmittalEditModal, SubmittalUtils } from '..';
import { WorkflowStateModal } from './workflow-state-modal';
import { WorkflowHistoryModal } from './workflow-history-modal';
import { ImageService } from '../../global/service/ImageService';
import { CalendarUtils } from 'src/components/concourse-schedule/utils/CalendarUtils';
import { CommentSubmit } from '../../global/models/CommentSubmit';

@Component({
  tag: 'concourse-submittal-detail',
  styleUrls: ['concourse-submittals.css', '../../global/app.css'],
  shadow: true
})
export class ConcourseSubmittalDetail {
  static async present(projectId: string, submittalId: string, cssClass: string): Promise<void> {
    const modal = await modalController.create({
      component: 'concourse-submittal-detail',
      componentProps: { submittalId, projectId, isSidebarView: true, showLogo: false },
      cssClass: 'concourse-modal ' + cssClass,
      backdropDismiss: false,
    });

    return new Promise(async resolve => {
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  // -------------------------
  // PROPS, STATE & EVENTS
  // -------------------------

  @Element() el: HTMLElement;

  @Prop() projectId: string;
  @Prop() submittalId: string;
  @Prop() isModal: boolean;
  @Prop() isSidebarView: boolean;

  @State() submittal: SubmittalDoc;
  @State() approvalStatus: string = 'pending';
  @State() isStateActionLoading: boolean = false;
  @State() showRevisionModal: boolean = false;
  @State() revisionConfirmModalOpen: boolean = false;
  @State() files: FileDoc[] = [];
  @State() scheduleTasks: TaskDoc[] = [];
  @State() feed: SubmittalFeedItemDoc[] = [];
  @State() selectedRevisionIndex: number = -1; // -1 means current revision (latest)
  @State() newComment: string = '';
  @State() feedSelected: SubmittalFeedItemDoc[] = [];
  @State() loading: boolean = false;
  @State() error: string = null;
  @State() detailsExpanded: boolean = false;
  @State() descriptionTextExpanded: boolean = false;
  @State() activeFilter: string = 'all';
  @State() calendar: Calendar;
  @State() scheduleData: ScheduleData;

  @Event() dismiss: EventEmitter;
  //@Event() closeClick?: EventEmitter<void>;

  // -------------------------
  // PRIVATE FIELDS
  // -------------------------

  private commentsRef: HTMLDivElement;

  private project: ProjectDoc;

  private submittalsDbListener: DbListener = {
    onComplete: (docs: SubmittalDoc[]) => {
      this.handleSubmittalChange(docs);
    },
    onChange: (event: ChangeEvent) => {
      if (event.direction === 'pull') {
        this.handleSubmittalChange(event.docs);
      }
    },
    onError: (error: Error) => {
      console.error('Submittals DB error:', error);
      this.error = error.message;
    },
  };

  private feedDbListener: DbListener = {
    onComplete: (docs: SubmittalFeedItemDoc[]) => {
      this.feed = this.filterFeedForSubmittal(docs);
    },
    onChange: (event: ChangeEvent) => {
      this.feed = this.filterFeedForSubmittal(event.docs);
    },
    onError: (error: Error) => {
      console.error('Feed DB error:', error);
    },
  };

  private filesDbListener: DbListener = {
    onComplete: (docs: FileDoc[]) => {
      this.files = docs;
    },
    onChange: (event: ChangeEvent) => {
      this.files = event.docs;
    },
    onError: (error: Error) => {
      console.error('Files DB error:', error);
    },
  };

  private filterFeedForSubmittal(docs: SubmittalFeedItemDoc[]): SubmittalFeedItemDoc[] {
    return docs.filter(item => item.type === FeedItemType.SUBMITTAL && item.related_id === this.submittalId).sort(FeedService.sortByDateAsc);
  }

  // -------------------------
  // GETTERS
  // -------------------------

  get user(): User {
    return globalThis.user;
  }

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  get isMobileOrSidebar(): boolean {
    return this.isMobile || this.isSidebarView;
  }
  get canRead(): boolean {
    return RoleUtils.canRead(this.user.roles, this.projectId, 'submittals', this.submittalId);
  }

  get canWrite(): boolean {
    return this.currentState && this.selectedIsLatest && this.user.hasOneOfRoles(SubmittalUtils.getWriteRoles(this.projectId, this.submittalId));
  }

  get isAdmin(): boolean {
    return this.user.canAdministerProject(this.projectId);
  }

  get isApproved(): boolean {
    const lastStatus = this.statusHistory[this.statusHistory.length - 1];
    return lastStatus?.state_id === Constants.SUBMITTAL_APPROVAL_STATUS_ID;
  }

  get canCreateRevision(): boolean {
    return this.selectedIsLatest && this.user.hasOneOfRoles(SubmittalUtils.getWriteRoles(this.projectId, this.submittalId)) && !this.isApproved;
  }

  get latestRevision(): SubmittalRevision {
    return this.submittal.revisions[this.submittal.revisions.length - 1];
  }

  get revision(): SubmittalRevision {
    if (this.selectedRevisionIndex === -1) {
      return this.latestRevision;
    }
    return this.submittal.revisions[this.selectedRevisionIndex];
  }

  get statusHistory(): WorkflowStateComplete[] {
    return SubmittalUtils.getRevisionStateHistory(this.revision);
  }

  get currentState(): WorkflowState {
    const state = SubmittalUtils.getRevisionState(this.submittal, this.revision);
    return state;
  }

  get currentStateIndex(): number {
    return SubmittalUtils.getRevisionStateIndex(this.submittal, this.revision);
  }

  get activeBubbleIndex(): number {
    return this.statusHistory.length;
  }

  get selectedIsLatest(): boolean {
    return this.selectedRevisionIndex === -1 || this.selectedRevisionIndex === this.submittal.revisions.length - 1;
  }

  get revisionStatusHistory(): WorkflowStateComplete[] {
    return this.revision.status_history;
  }

  get revisionFiles(): FileDoc[] {
    const fileIds = this.feed.filter(item => item.sub === FeedItemSubType.FILE && !item.deleted_date).map(item => item.sub_id);
    return this.files.filter(file => fileIds.includes(file._id));
  }

  get dueDate(): Date | null {
    return this.submittal.due_date ? DateUtils.fromISO(this.submittal.due_date) : null;
  }

  get submitStep(): WorkflowState {
    return this.submittal.states.find(s => s.id === Constants.SUBMITTAL_SUBMIT_STATUS_ID);
  }

  get submitPeople(): PersonDoc[] {
    return this.people.filter(p => this.submitStep?.dueBy.includes(p._id));
  }

  get people(): PersonDoc[] {
    return peopleState.people;
  }

  // -------------------------
  // LIFECYCLE
  // -------------------------

  async componentWillLoad() {
    if (!this.canRead) return;
    await this.loadSubmittalData();
    if (!this.submittal) {
      return;
    }

    await this.loadFilesData();

    // I think we need to move calendars off 'schedule' and to 'project' since its a universal resource.
    // there are some users who don't have schedule permissions but still need to see the calendar.
    this.calendar = CalendarUtils.getDefaultCalendar();

    if (this.user.getSchedulePermissions(this.projectId).canRead) {
      this.scheduleData = new ScheduleData(this.projectId);
      this.scheduleData.addEventListener(ScheduleData.LOAD_COMPLETE, this.onScheduleDataLoadComplete);
      this.scheduleData.loadScheduleData();
    }

    this.project = await ProjectService.getProjectDoc(this.projectId);

    if (this.isMobile && this.isSidebarView && !this.isModal) {
      this.showFullScreen();
    }

    await this.loadFeedData();
  }

  /** Stable reference for add/removeEventListener */
  private readonly onScheduleDataLoadComplete = (_e: CustomEvent) => {
    this.scheduleTasks = this.scheduleData.tasks;
  };

  async componentDidLoad() {
    SharedUtils.scrollToBottom(this.commentsRef);
  }

  componentDidUpdate() {
    if (!this.detailsExpanded) {
      SharedUtils.scrollToBottom(this.commentsRef);
    }
  }

  async disconnectedCallback() {
    if (this.scheduleData) {
      this.scheduleData.dispose();
      this.scheduleData.removeEventListener(ScheduleData.LOAD_COMPLETE, this.onScheduleDataLoadComplete);
      this.scheduleData = null;
    }
    this.scheduleData = null;
    const submittalsDb = SubmittalService.getSubmittalsDb(this.projectId);
    submittalsDb?.removeListeners(this.submittalsDbListener);

    const filesDb = FileService.getFilesDb(this.projectId);
    filesDb?.removeListeners(this.filesDbListener);

    const feedDb = FeedService.getDb(this.projectId);
    feedDb?.removeListeners(this.feedDbListener);
  }

  private async loadSubmittalData() {
    try {
      this.loading = true;
      this.error = null;

      const submittalsDb = SubmittalService.getSubmittalsDb(this.projectId);
      submittalsDb.addListeners(this.submittalsDbListener);
      await submittalsDb.syncRemote();

      if (!this.submittal) {
        return;
      }
      if (!this.currentState) {
        this.activeFilter = 'accepted-answers';
      }
    } catch (error) {
      console.error('Error loading submittal:', error);
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      this.loading = false;
    }
  }

  private async loadFeedData() {
    const feedDb = FeedService.getDb(this.projectId);
    feedDb.addListeners(this.feedDbListener);
    await feedDb.syncRemote();
  }

  private async loadFilesData() {
    const filesDb = FileService.getFilesDb(this.projectId);
    filesDb.addListeners(this.filesDbListener);
    await filesDb.syncRemote();
  }

  // -------------------------
  // DATA / SAVE
  // -------------------------

  handleSubmittalChange(docs: SubmittalDoc[]) {
    if (this.loading) {
      this.loading = false;
    }
    this.submittal = docs.find(doc => doc._id === this.submittalId && !doc.deleted_date);

    if (!this.submittal) return;
  }

  async saveSubmittal(): Promise<void> {
    if (!this.submittal) return;
    try {
      this.submittal = await SubmittalService.updateSubmittal(this.projectId, this.submittal);
    } catch (error) {
      console.error('Error saving submittal:', error);
      const alert = await alertController.create({
        header: 'Error',
        message: `Failed to save submittal: ${error.message}`,
        buttons: ['OK'],
      });
      await alert.present();
    }
  }

  createRevision(): void {
    if (!this.canCreateRevision) return;
    if (!this.submittal.states || this.submittal.states.length === 0) return;
    this.revisionConfirmModalOpen = true;
  }

  async doCreateRevision(): Promise<void> {
    this.revisionConfirmModalOpen = false;
    const prevRevision = this.revision;
    const newNum = prevRevision && prevRevision.num ? (parseInt(prevRevision.num, 10) + 1).toString() : '1';
    const newRevision: SubmittalRevision = {
      num: newNum,
      created_by: this.user.id,
      created_date: DateUtils.currentIso,
      status_history: [
        {
          state_id: this.submittal.states[0].id,
          user_id: this.user.id,
          date: DateUtils.currentIso,
        },
      ],
      accepted_answers: [],
      files: [],
    };
    this.submittal.revisions = [...this.submittal.revisions, newRevision];
    await this.saveSubmittal();
    await this.addFeedItem(FeedItemSubType.CREATE, newNum, `(Rev ${newNum})`);
  }

  selectRevision(index: number): void {
    this.selectedRevisionIndex = index;
  }

  onRemoveTaskClick(relationship: TaskRelationship): void {
    this.submittal.successors = this.submittal.successors.filter(d => d.task_unique_id !== relationship.task_unique_id);
    this.saveSubmittal();
  }

  // -------------------------
  // NAVIGATION & MODALS
  // -------------------------

  handleHeaderDismiss() {
    //this.closeClick?.emit();
    if (this.isModal || this.isSidebarView) {
      this.dismiss.emit();
    } else {
      window.location.hash = `/project/${this.projectId}/submittals`;
    }
  }

  async openEditModal() {
    if (!this.canWrite) return;
    const saved = await SubmittalEditModal.present(this.project, this.submittal);
    if (saved) {
      this.submittal = saved;
    }
  }

  toggleDescriptionText() {
    this.descriptionTextExpanded = !this.descriptionTextExpanded;
  }

  setFilter(filter: string) {
    this.activeFilter = filter;
  }

  showFile(file: FileDoc) {
    DocumentViewerModal.present(this.projectId, file);
  }

  openDueDateSelector(): void {
    if (!this.canWrite) return;
    DateSelector.presentModal(this.submittal.due_date, async (date: string) => {
      if (date) {
        this.submittal.due_date = date;
        await this.saveSubmittal();
      }
    });
  }

  // -------------------------
  // WORKFLOW / STATES
  // -------------------------

  async moveToPrevState(state: WorkflowState | null, actor: PersonDoc): Promise<void> {
    const message = await ConcourseMessageModal.present(
      `Return submittal to ${actor.first_name}`,
      'Please provide a message explaining why this submittal is being returned.',
      'Return',
    );

    if (message) {
      try {
        const stateId = state ? state.id : null;
        this.submittal = await SubmittalService.completeState(this.projectId, this.submittal, stateId, [actor._id], message);

        toastController
          .create({
            message: `The submittal has been returned to ${actor.first_name}`,
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
      } catch (error) {
        console.error('Error returning submittal:', error);
      }
    }
  }

  async doStateAction(): Promise<void> {
    this.isStateActionLoading = true;

    try {
      const currentState = this.currentState;
      const isResponsible = currentState.dueBy?.includes(this.user.id);
      if (currentState.dueBy && currentState.dueBy.length > 0 && !isResponsible) {
        if (!(await this.showNotResponsibleAlert(currentState, this.isAdmin))) {
          return;
        }
      }
      if (currentState.id == Constants.SUBMITTAL_APPROVAL_STATUS_ID && this.revision.accepted_answers.length === 0) {
        return this.showNowAnswersSelectedAlert();
      }

      const nextStateHasAssignees = WorkflowUtils.validateNextStateHasAssignees(this.submittal.states, this.currentStateIndex);

      if (!nextStateHasAssignees) {
        const people = await ConcoursePersonSelector.present({
          projectId: this.projectId,
          feature: 'submittals',
          featureId: this.submittalId,
          peopleToOmitIds: currentState.dueBy,
          multiSelect: true,
          needsWrite: true,
          modalTitle: 'Choose who will be responsible for this state',
        });
        if (people && people.length > 0) {
          this.submittal.states[this.currentStateIndex + 1].dueBy = people.map(p => p._id);
          await this.completeState(currentState);
        }
      } else {
        await this.completeState(currentState);
      }
    } finally {
      this.isStateActionLoading = false;
    }
  }

  async showNotResponsibleAlert(currentState: WorkflowState, isAdmin: boolean): Promise<boolean> {
    const responsibleNames = currentState.dueBy
      .map(personId => {
        const person = PeopleService.findPerson(this.people, personId);
        return person ? person.first_name + ' ' + person.last_name : null;
      })
      .join(', ');

    return new Promise(async resolve => {
      const buttons = isAdmin
        ? [
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => resolve(false),
            },
            {
              text: 'Proceed Anyway',
              role: 'confirm',
              handler: () => resolve(true),
            },
          ]
        : [{ text: 'OK', role: 'confirm', handler: () => resolve(false) }];
      const alert = await alertController.create({
        header: 'You are not responsible for this state',
        message: `Only ${responsibleNames} may ${currentState.action} this submittal`,
        buttons: buttons,
      });
      await alert.present();
    });
  }

  async showNowAnswersSelectedAlert() {
    const alert = await alertController.create({
      header: 'No answers selected',
      message: 'Please approve one of the responses below',
      buttons: ['OK'],
    });
    await alert.present();
  }

  async completeState(state: WorkflowState): Promise<void> {
    this.submittal = await SubmittalService.completeState(this.projectId, this.submittal, state.id);
    toastController
      .create({
        message: `The submittal has been ${state.pastTense}`,
        duration: 2000,
        position: 'bottom',
      })
      .then(toast => toast.present());
  }

  async onBarClick(index: number) {
    console.log('Showing pre-pending item:', index);
    if (!this.canWrite) return;
    const state: WorkflowState = {
      id: null,
      dueBy: [],
      dueDays: 0,
      action: '',
      pastTense: '',
    };
    const updated = await WorkflowStateModal.present(this.projectId, state, 'submittals', this.submittalId, false);
    if (updated && (updated as WorkflowState)) {
      const stateIndex = this.currentStateIndex + index - 1;
      this.submittal.states.splice(stateIndex, 0, { ...(updated as WorkflowState), id: ConcourseDb.makeShortDocId() });
      await this.saveSubmittal();
    }
  }

  async onBubbleClick(indexes: number[]) {
    if (indexes.length == 1 && indexes[0] >= this.statusHistory.length) {
      const stateIndex = indexes[0] - this.statusHistory.length + this.currentStateIndex;
      const state = this.submittal.states[stateIndex];
      const canDelete = SubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW.states.find(s => s.id === state.id) === undefined;
      const updated = await WorkflowStateModal.present(this.projectId, state, 'submittals', this.submittalId, canDelete);
      if (updated && (updated as WorkflowState)) {
        this.submittal.states[stateIndex] = updated as WorkflowState;
        await this.saveSubmittal();
        toastController
          .create({
            message: 'Submittal state updated successfully',
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
      }
      if (updated && typeof updated === 'string') {
        console.warn('Submittal state to be deleted:', updated);
        this.submittal.states = this.submittal.states.filter((s, idx) => idx !== stateIndex);
        await this.saveSubmittal();
        toastController
          .create({
            message: 'Submittal state deleted successfully',
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
        console.log('Submittal state deleted:', updated);
      }
    } else {
      const history = this.statusHistory.filter((_, idx) => indexes.includes(idx));
      const selected = await WorkflowHistoryModal.presentModal(this.people, this.submittal.states, history);
      if (selected) {
        console.log('Selected event:', selected);
        const stateIndex = this.submittal.states.findIndex(s => s.id === selected.id);
        const lastCompleted = this.submittal.states[stateIndex - 1];
        //this.setLastCompletedState(lastCompleted);
      }
    }
  }

  // -------------------------
  // COMMENTS
  // -------------------------

  async onCommentSubmit(detail: CommentSubmit) {
    const { message, files, people } = detail;
    for (const person of people) {
      await this.addDistributionPerson(person);
    }
    if (!files.length) {
      if (message) await this.addComment(message);
      else this.newComment = '';
      return;
    }
    for (const file of files) {
      const sub = file.type === FileType.PHOTO ? FeedItemSubType.PHOTO : FeedItemSubType.FILE;
      await this.addFeedItem(sub, file._id, message || null);
      if (file.type === FileType.PHOTO) {
        const analysis: PhotoAnalysis = await ImageService.analyzePhoto(this.projectId, file.s3_key);
        await FileService.savePhoto(this.projectId, { ...(file as PhotoDoc), description: analysis.description, tags: analysis.tags });
      }
    }
    this.newComment = '';
    this.activeFilter = 'all';
    SharedUtils.scrollToBottom(this.commentsRef, 200);
  }

  async addDistributionPerson(person: PersonDoc) {
    const distribution = this.submittal.distribution || [];
    if (!distribution.includes(person._id)) {
      distribution.push(person._id);
      this.submittal.distribution = distribution;
      await this.saveSubmittal();
    }
  }

  // -------------------------
  // FEED ITEMS
  // -------------------------

  async addFeedItem(sub: FeedItemSubType, sub_id: string, text: string = null) {
    const item: SubmittalFeedItemDoc = {
      ...FeedService.newFeedItem(this.user.id, FeedItemType.SUBMITTAL, this.submittalId, sub, sub_id, text),
      revision: this.revision.num,
    };
    try {
      await FeedService.storeFeedItem(this.projectId, item);
      SharedUtils.scrollToBottom(this.commentsRef, 200);
    } catch (error) {
      console.error('Error adding feed item:', error);
    }
  }

  async addComment(message?: string): Promise<void> {
    const text = message ?? this.newComment;
    if (!text || !this.canWrite) return;
    await this.addFeedItem(FeedItemSubType.COMMENT, null, text);
    this.newComment = '';
    this.activeFilter = 'all';
  }

  async removeFile(file: FileDoc) {
    const itemIndex = this.feed.findIndex(item => item.sub == FeedItemSubType.FILE && item.sub_id == file._id);
    if (itemIndex) {
      this.feed[itemIndex].deleted_by = this.user.id;
      this.feed[itemIndex].deleted_date = DateUtils.currentIso;
      await this.saveSubmittal();
    }
  }

  async handleFinishSubmittalMarkup(fileId: string) {
    await this.addFeedItem(FeedItemSubType.MARKED, fileId, 'marked the document');
  }

  // -------------------------
  // RENDER
  // -------------------------

  headerButtons(returnToState: WorkflowState, prevActor: PersonDoc, isResponsible: boolean) {
    const isInLatestRevision = this.latestRevision?.num === this.submittal.revisions[this.submittal.revisions.length - 1].num;
    if (this.isSidebarView || !isInLatestRevision) {
      return null;
    }
    return [
      this.canWrite && (
        <ion-button data-testId="submittal-edit-button" fill="clear" shape="round" class="" onClick={() => this.openEditModal()}>
          <ion-icon slot="icon-only" src="/assets/icon/pencil.svg"></ion-icon>
        </ion-button>
      ),
      this.canWrite && returnToState !== undefined && (
        <ion-button
          data-testId="submittal-return-button"
          color="primary"
          fill="outline"
          shape="round"
          class="ion-margin-end"
          onClick={() => this.moveToPrevState(returnToState, prevActor)}
        >
          {this.isMobileOrSidebar ? <ion-label>Return</ion-label> : <ion-label>Return to {PeopleService.getFirstName(prevActor)}</ion-label>}
        </ion-button>
      ),
      isResponsible && (
        <ion-button
          data-testId="submittal-next-action"
          color="primary"
          fill="solid"
          shape="round"
          class="ion-margin-end"
          onClick={() => this.doStateAction()}
          disabled={this.isStateActionLoading}
        >
          {this.isStateActionLoading ? <ion-spinner name="crescent" slot="start"></ion-spinner> : null}
          {this.currentState.action}
        </ion-button>
      ),
    ];
  }

  async showFullScreen() {
    ConcourseSubmittalDetail.present(this.projectId, this.submittalId, 'modal-full-view');
    if (this.isSidebarView) {
      this.dismiss.emit();
    }
  }

  render() {
    if (!this.canRead) {
      return (
        <Fragment>
          {this.isSidebarView ? (
            <sidepanel-header
              number={this.submittal?.number ?? '—'}
              subject={this.submittal?.title ?? 'Submittal'}
              onShowFullscreen={() => undefined}
              onCloseClick={() => this.dismiss.emit()}
            />
          ) : (
            <concourse-header
              showHamburger={true}
              showLogo={false}
              showBack={true}
              onBack={() => this.handleHeaderDismiss()}
              projectId={this.projectId}
              pageTitle={`Loading submittal...`}
            ></concourse-header>
          )}
          <ion-content
            class="submittal-detail__no-access-content"
            style={{ '--background': this.isSidebarView ? 'var(--background-color-step-50)' : 'var(--ion-background-color)' }}
          >
            <div class="submittal-detail__no-access ion-padding">
              <p class="submittal-detail__no-access-title">You don't have access to this submittal</p>
              <p class="submittal-detail__no-access-body">Your project role does not allow viewing submittal details.</p>
            </div>
          </ion-content>
        </Fragment>
      );
    }

    if (this.error) {
      return (
        <ion-content>
          <div class="submittal-detail__load-error ion-padding">
            <p class="submittal-detail__load-error-title">Something went wrong</p>
            <p class="submittal-detail__load-error-body">{this.error}</p>
          </div>
        </ion-content>
      );
    }

    if (!this.submittal) {
      return (
        <div class="submittal-detail__loading ion-padding">
          <ion-spinner name="crescent" />
        </div>
      );
    }
    const revision = this.revision;
    const statusHistory = revision?.status_history || [];
    const { stateComplete, returnToState, isResponsible } = WorkflowUtils.getWorkflowRenderState(this.submittal.states, statusHistory, this.currentState, this.user?.id);

    const prevActor = this.people.find(p => p._id === stateComplete?.user_id);
    const numberAndRevision = `Submittal ${this.submittal.number} Rev ${this.revision.num}`;
    return (
      <Fragment>
        {this.isSidebarView ? (
          <sidepanel-header
            number={this.submittal.number}
            subject={this.submittal.title}
            onShowFullscreen={() => this.showFullScreen()}
            showClose={true}
            onCloseClick={() => this.dismiss.emit()}
          />
        ) : (
          <concourse-header
            showBack={true}
            onBack={() => this.handleHeaderDismiss()}
            projectId={this.projectId}
            pageTitle={`${numberAndRevision} - ${this.submittal.title}`}
            shareRoles={this.isMobileOrSidebar ? null : [`p-${this.projectId}-submittals-${this.submittalId}`]}
          >
            {this.headerButtons(returnToState, prevActor, isResponsible)}
          </concourse-header>
        )}
        <ion-content style={{ '--background': this.isSidebarView ? 'var(--background-color-step-50)' : 'var(--ion-background-color)' }}>
          <ion-grid class="ion-no-padding ion-display-flex ion-flex-column" style={{ height: '100%' }}>
            <ion-row class="flex-grow workflow-view__content-container">
              <ion-col size-md={this.isMobileOrSidebar ? '12' : '8'} class="vertical-scroll cc-scrollbars">
                {this.detailsExpanded ? (
                  <submittal-details-card
                    submittal={this.submittal}
                    people={this.people}
                    scheduleTasks={this.scheduleTasks}
                    selectedRevisionIndex={this.selectedRevisionIndex}
                    detailsExpanded={this.detailsExpanded}
                    descriptionTextExpanded={this.descriptionTextExpanded}
                    canCreateRevision={this.canCreateRevision}
                    projectId={this.projectId}
                    removeTaskClick={(relationship: TaskRelationship) => this.onRemoveTaskClick(relationship)}
                    createRevision={() => this.createRevision()}
                    selectRevision={(index: number) => this.selectRevision(index)}
                    toggleDescriptionText={() => this.toggleDescriptionText()}
                  />
                ) : (
                  <div class="ion-display-flex ion-flex-column workflow-view__left-column">
                    <div class="ion-padding-horizontal workflow-view__timeline-container">
                      <concourse-timeline
                        bubbles={TimelineUtils.getTimelineBubblesFromRevision(this.revision, this.submittal.states, this.people, this.calendar)}
                        activeIndex={this.activeBubbleIndex}
                        dueDate={this.dueDate}
                        onBarClick={e => this.onBarClick(e.detail)}
                        onBubbleClick={e => this.onBubbleClick(e.detail)}
                        onDueDateClick={e => this.openDueDateSelector()}
                      />
                    </div>
                    <submittal-comments-card
                      class="ion-flex-1 workflow-view__comments-container"
                      projectId={this.projectId}
                      submittalId={this.submittalId}
                      submittal={this.submittal}
                      people={this.people}
                      files={this.files}
                      feed={this.feed}
                      selectedRevisionIndex={this.selectedRevisionIndex}
                      canWrite={this.canWrite}
                      isMobile={this.isMobileOrSidebar}
                      newComment={this.newComment}
                      activeFilter={this.activeFilter}
                      onSetFilter={(e: CustomEvent<string>) => this.setFilter(e.detail)}
                      onCommentSubmit={(e: CustomEvent<CommentSubmit>) => this.onCommentSubmit(e.detail)}
                      onNewCommentChange={(e: CustomEvent<string>) => (this.newComment = e.detail)}
                      onFeedSelectedChange={(e: CustomEvent<SubmittalFeedItemDoc[]>) => (this.feedSelected = e.detail)}
                      onHandleFinishSubmittalMarkup={(e: CustomEvent<string>) => this.handleFinishSubmittalMarkup(e.detail)}
                      onSetCommentsRef={(e: CustomEvent<HTMLDivElement>) => (this.commentsRef = e.detail)}
                      onSaveSubmittal={() => this.saveSubmittal()}
                    />
                  </div>
                )}
              </ion-col>
              {!this.isMobileOrSidebar && (
                <ion-col size-md="4" class="workflow-view-detail-info-container vertical-scroll cc-scrollbars ion-hide-md-down">
                  <submittal-details-card
                    submittal={this.submittal}
                    people={this.people}
                    scheduleTasks={this.scheduleTasks}
                    selectedRevisionIndex={this.selectedRevisionIndex}
                    detailsExpanded={this.detailsExpanded}
                    descriptionTextExpanded={this.descriptionTextExpanded}
                    canCreateRevision={this.canCreateRevision}
                    projectId={this.projectId}
                    removeTaskClick={(relationship: TaskRelationship) => this.onRemoveTaskClick(relationship)}
                    createRevision={() => this.createRevision()}
                    selectRevision={(index: number) => this.selectRevision(index)}
                    toggleDescriptionText={() => this.toggleDescriptionText()}
                  />
                </ion-col>
              )}
            </ion-row>
            {this.isMobileOrSidebar && <workflow-view-footer detailsExpanded={this.detailsExpanded} onDetailsExpandedChange={e => (this.detailsExpanded = e.detail)} />}
          </ion-grid>
        </ion-content>
        {this.revisionConfirmModalOpen && (
          <ion-modal cssClass="concourse-modal" isOpen={this.revisionConfirmModalOpen} backdropDismiss={false} onDidDismiss={() => (this.revisionConfirmModalOpen = false)}>
            <revision-confirmation-modal
              class="concourse-modal-implicit concourse-modal modal-height-auto"
              onConfirm={() => this.doCreateRevision()}
              onDismiss={() => (this.revisionConfirmModalOpen = false)}
            />
          </ion-modal>
        )}
      </Fragment>
    );
  }
}
